import type { Attachment } from 'svelte/attachments';

interface SwipeCloseOptions {
    onClose: () => void;
    enabled?: boolean;
    threshold?: number;
}

export const swipeClose = (options: SwipeCloseOptions): Attachment<HTMLElement> => {
    const { onClose, enabled = true, threshold = 100 } = options;

    return (node: HTMLElement) => {
        if (!enabled) return;

        let startY = 0;
        let currentTranslate = 0;
        let isDragging = false;

        const getScrollParent = (target: EventTarget | null) => {
            let el = target as HTMLElement;
            while (el && el !== node) {
                if (el.classList.contains('overflow-y-auto')) return el;
                el = el.parentElement!;
            }
            return null;
        };

        const handleStart = (e: TouchEvent) => {
            const scrollEl = getScrollParent(e.target);
            // Only allow swipe if we are NOT in a scrolled container
            if (scrollEl && scrollEl.scrollTop > 0) return;

            startY = e.touches[0].clientY;
            isDragging = true;
            node.style.transition = 'none';
        };

        const handleMove = (e: TouchEvent) => {
            if (!isDragging) return;
            const currentY = e.touches[0].clientY;
            const diff = currentY - startY;

            if (diff > 0) {
                if (e.cancelable) e.preventDefault();
                currentTranslate = diff;
                node.style.transform = `translateY(${diff}px)`;
            } else if (diff < 0) {
                isDragging = false;
                node.style.transform = '';
                currentTranslate = 0;
            }
        };

        const handleEnd = () => {
            if (!isDragging) return;
            isDragging = false;
            node.style.transition = 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)';

            if (currentTranslate > threshold) {
                onClose();
            } else {
                node.style.transform = '';
            }
            currentTranslate = 0;
        };

        node.addEventListener('touchstart', handleStart, { passive: true });
        node.addEventListener('touchmove', handleMove, { passive: false });
        node.addEventListener('touchend', handleEnd);

        return () => {
            node.removeEventListener('touchstart', handleStart);
            node.removeEventListener('touchmove', handleMove);
            node.removeEventListener('touchend', handleEnd);
        };
    };
};